// src/styles/StyledComponents.js
import React from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import Spinner from "../components/AppSpinner.mjs";

export const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
  50% { transform: scale(1.02); box-shadow: 0 0 0 10px rgba(139, 92, 246, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(139, 92, 246, 0); }
`;

export const Overlay = styled(motion.div)`
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

/* The ONE app-wide spinner: a quiet ring with the accent on top. Size-aware
   (default 48px) so the same component covers page loads, section loads, and
   inline button spinners — use <Spinner /> or <Spinner size={18} /> everywhere
   instead of hand-rolling rings. */
export { Spinner };

const CenteredLoaderWrap = styled.div`
  display: grid;
  width: 100%;
  min-height: ${({ $minHeight }) => $minHeight || "100vh"};
  place-items: center;
  background: ${({ $background }) => $background || "transparent"};
`;

export const CenteredLoader = ({
  size = 44,
  minHeight = "100vh",
  background = "transparent",
  label = "Loading",
}) => (
  <CenteredLoaderWrap
    $minHeight={minHeight}
    $background={background}
    role="status"
    aria-label={label}
    data-centered-loader="true"
  >
    <Spinner size={size} aria-hidden="true" data-app-spinner="true" />
  </CenteredLoaderWrap>
);

/* Canonical loading surface: the ring, centred, with optional muted label.
   Use for every page / section / panel that waits on data, so loading looks
   the same everywhere. */
const PageLoaderWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: ${({ $minHeight }) => $minHeight || "55vh"};
  color: ${({ theme }) => theme.textSecondary};
  text-align: center;
  span { font-size: 0.92rem; font-weight: 600; letter-spacing: 0.01em; }
`;

export const PageLoader = ({ label, size, minHeight }) => (
  <PageLoaderWrap $minHeight={minHeight} role="status" aria-label={label || "Loading"}>
    <Spinner size={size} />
    {label ? <span>{label}</span> : null}
  </PageLoaderWrap>
);

export const ErrorBanner = styled(motion.div)`
  background: ${({ theme }) => theme.errorBg};
  color: ${({ theme }) => theme.errorText};
  padding: 16px;
  text-align: center;
  font-weight: 500;
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  min-width: 300px;
  border: 1px solid ${({ theme }) => theme.errorText}40;
`;

export const Container = styled(motion.div)`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 20px 80px;
  @media (max-width: 600px) {
    padding: 20px 16px 60px;
  }
`;

export const Card = styled(motion.div)`
  background: ${({ theme }) => theme.cardBg};
  backdrop-filter: ${({ theme }) => theme.backdropFilter};
  -webkit-backdrop-filter: ${({ theme }) => theme.backdropFilter};
  border: 1px solid ${({ theme }) => theme.cardBorder};
  border-radius: var(--radius-md);
  padding: 32px;
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;

  @media (max-width: 600px) {
    padding: 24px;
  }
`;

export const GhostCard = styled.div`
  background: ${({ theme }) => theme.isDark ? '#1e293b50' : '#f1f5f950'};
  border: 1px solid ${({ theme }) => theme.isDark ? '#33415550' : '#e2e8f080'};
  border-radius: var(--radius-md);
  padding: 24px;
`;

export const TopMetricsContainer = styled.div`
  display: flex;
  background: ${({ theme }) => theme.isDark ? '#1e293b40' : '#f1f5f950'};
  border: 1px solid ${({ theme }) => theme.isDark ? '#33415550' : '#e2e8f080'};
  border-radius: var(--radius-md);
  overflow: hidden;
  margin-bottom: 64px;
  
  > div {
    flex: 1;
    padding: 24px;
    border-right: 1px solid ${({ theme }) => theme.isDark ? '#33415550' : '#e2e8f080'};
    
    &:last-child {
      border-right: none;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    > div {
      border-right: none;
      border-bottom: 1px solid ${({ theme }) => theme.isDark ? '#33415550' : '#e2e8f080'};
      &:last-child { border-bottom: none; }
    }
  }
`;

export const Button = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 24px;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  border-radius: var(--radius-sm);
  cursor: pointer;
  /* Calm, solid accent instead of a gradient + coloured glow. The filled fill
     uses primaryHover (the deeper indigo) so white label text keeps contrast. */
  background: ${({ theme, variant }) =>
    variant === 'secondary' ? 'transparent' :
      variant === 'outline' ? 'transparent' :
        theme.primaryHover};
  color: ${({ theme, variant }) =>
    variant === 'secondary' ? theme.textSecondary :
      variant === 'outline' ? theme.primary :
        "#fff"};
  border: 1px solid ${({ theme, variant }) =>
    variant === 'outline' ? theme.primary :
      variant === 'secondary' ? theme.border :
        'transparent'};
  box-shadow: ${({ variant }) => (variant === 'secondary' || variant === 'outline') ? 'none' : 'var(--shadow-sm)'};
  transition: background var(--dur) var(--ease), border-color var(--dur) var(--ease),
              color var(--dur) var(--ease), box-shadow var(--dur) var(--ease), transform var(--dur-fast) var(--ease);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${({ variant }) => (variant === 'secondary' || variant === 'outline') ? 'none' : 'var(--shadow-md)'};
    background: ${({ theme, variant }) =>
      variant === 'outline' ? `${theme.primary}14` :
      variant === 'secondary' ? 'rgba(255, 255, 255, 0.05)' :
      null};
    color: ${({ theme, variant }) => variant === 'secondary' ? theme.text : null};
    border-color: ${({ theme, variant }) => variant === 'secondary' ? theme.textMuted : null};
  }

  &:active:not(:disabled) { transform: translateY(0); }

  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

  &:disabled {
    background: ${({ theme, variant }) => (variant === 'secondary' || variant === 'outline') ? 'transparent' : theme.border};
    color: ${({ theme }) => theme.textMuted};
    border-color: ${({ theme, variant }) => variant === 'outline' ? theme.border : 'transparent'};
    box-shadow: none;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const ProgressBar = styled.div`
  height: 10px;
  background: ${({ theme }) => theme.cardBorder};
  border-radius: var(--radius-pill);
  overflow: hidden;
  margin-bottom: 24px;
  position: relative;
`;

export const Progress = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, ${({ theme }) => theme.primary}, ${({ theme }) => theme.secondary});
  border-radius: var(--radius-pill);
  box-shadow: 0 0 10px ${({ theme }) => theme.primary}80;
`;

export const OptionGrid = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
  margin-top: 24px;

  /* 2-column only on wide monitors where there's room to breathe */
  @media (min-width: 1440px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }
`;

export const OptionItem = styled(motion.div)`
  background: ${({ selected, theme, $state }) => {
    if ($state === 'correct-selected') return 'rgba(34, 197, 94, 0.08)';
    if ($state === 'wrong-selected') return 'rgba(239, 68, 68, 0.10)';
    if ($state === 'missed-correct') return 'rgba(59, 130, 246, 0.05)';
    if ($state === 'neutral') return 'rgba(255, 255, 255, 0.02)';
    return selected ? 'rgba(217, 119, 6, 0.08)' : 'transparent';
  }};
  border: ${({ $state, selected }) => {
    if ($state === 'correct-selected') return '2px solid #16A34A';
    if ($state === 'wrong-selected') return '2px solid #DC2626';
    if ($state === 'missed-correct') return '2px dashed rgba(59, 130, 246, 0.3)';
    if ($state === 'neutral') return '2px solid rgba(255, 255, 255, 0.03)';
    return selected ? '2px solid #D97706' : '2px solid rgba(255, 255, 255, 0.06)';
  }};
  box-shadow: ${({ selected, $state }) => {
    if (selected && !$state) return '0 0 12px rgba(217, 119, 6, 0.15)';
    return 'none';
  }};
  color: ${({ theme }) => theme.text};
  width: 100%;
  font-family: inherit;
  text-align: left;
  padding: 14px 20px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: clamp(0.9rem, 1.2vw, 1rem);
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  transition: border-color 0.2s, background 0.2s, transform 0.2s;

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  &:disabled { cursor: default; }

  @media (max-width: 600px) {
    padding: 12px 14px;
    gap: 10px;
  }

  @media (min-width: 1440px) {
    padding: 20px 24px;
    font-size: 1.05rem;
  }

  &:hover {
    border-color: ${({ selected, $state }) => !$state && !selected && '#D97706'};
    background: ${({ selected, $state }) => !$state && !selected && 'rgba(217, 119, 6, 0.05)'};
  }
`;

export const OptionLetter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${({ selected, theme, $state }) => {
    if ($state === 'correct-selected') return '#22C55E';
    if ($state === 'wrong-selected') return '#EF4444';
    if ($state === 'missed-correct') return 'transparent';
    return selected ? '#D97706' : 'rgba(255,255,255,0.05)';
  }};
  border: ${({ $state }) => $state === 'missed-correct' ? '2px dashed #3B82F6' : '2px solid transparent'};
  color: ${({ selected, theme, $state }) => {
    if ($state === 'missed-correct') return '#3B82F6';
    if ($state === 'neutral') return '#B0B7C3';
    return (selected || $state) ? '#fff' : '#9CA3AF';
  }};
  font-weight: 800;
  font-size: 0.9rem;
  transition: all 0.3s ease;
`;

export const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

export const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  background: ${({ theme, variant }) => variant === 'secondary' ? `${theme.secondary}15` : `${theme.primary}15`};
  color: ${({ theme, variant }) => variant === 'secondary' ? theme.secondary : theme.primary};
  padding: 4px 10px;
  border-radius: var(--radius-xs);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid ${({ theme, variant }) => variant === 'secondary' ? `${theme.secondary}30` : `${theme.primary}30`};
`;

export const BadgeGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

export const DiagnosisGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 16px;
`;

export const DiagnosisCard = styled(motion.div)`
  background: ${({ $status }) => 
    $status === 'hit' ? 'rgba(34, 197, 94, 0.06)' : 
    $status === 'missed' ? 'rgba(59, 130, 246, 0.04)' : 
    $status === 'trap' ? 'rgba(239, 68, 68, 0.08)' : 
    'rgba(255, 255, 255, 0.02)'};
  border: ${({ $status }) => 
    $status === 'hit' ? '1px solid #16A34A' : 
    $status === 'missed' ? '1px dashed rgba(59, 130, 246, 0.3)' : 
    $status === 'trap' ? '1px solid #DC2626' : 
    '1px solid rgba(255, 255, 255, 0.05)'};
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const DiagnosisHeader = styled.div`
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  background: ${({ $status }) => 
    $status === 'hit' ? 'rgba(34, 197, 94, 0.04)' : 
    $status === 'missed' ? 'rgba(59, 130, 246, 0.03)' : 
    $status === 'trap' ? 'rgba(239, 68, 68, 0.04)' : 
    'rgba(255, 255, 255, 0.01)'};

  @media (max-width: 600px) {
    align-items: flex-start;
  }
`;

export const DiagnosisStatus = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 8px;
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: ${({ $status }) => 
    $status === 'hit' ? '#22C55E' : 
    $status === 'missed' ? '#3B82F6' : 
    $status === 'trap' ? '#EF4444' : 
    '#B0B7C3'};

  @media (max-width: 600px) {
    align-items: flex-start;
    line-height: 1.4;
    letter-spacing: 0.8px;
    overflow-wrap: anywhere;
  }
`;

export const DiagnosisLabel = styled.div`
  color: ${({ $status }) => 
    $status === 'correct' ? '#10b981' : '#ef4444'};
  opacity: 0.8;
`;

export const DiagnosisBody = styled.div`
  padding: 16px;
  font-size: 0.85rem;
  line-height: 1.6;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.textSecondary};
  flex: 1;
`;

export const RatingGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.cardBorder};
  flex-wrap: wrap;
`;

export const RatingButton = styled(motion.button)`
  flex: 1;
  min-width: 80px;
  padding: 10px;
  border-radius: var(--radius-sm);
  border: 1px solid ${({ theme, color }) => color || theme.border};
  background: ${({ active, color }) => active ? color : 'transparent'};
  color: ${({ active, color }) => active ? '#fff' : color || 'inherit'};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  .label { font-size: 0.75rem; opacity: 0.8; }
`;

export const QuestCard = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.cardBorder}50;
  
  &:last-child {
    border-bottom: none;
  }
  
  .status-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ completed }) => completed ? '#10b98120' : 'var(--border)'};
    color: ${({ completed }) => completed ? '#10b981' : 'var(--textSecondary)'};
  }
  
  .quest-info {
    flex: 1;
    h5 { margin: 0; color: var(--text); font-size: 1rem; }
    p { margin: 2px 0 0; color: var(--textSecondary); font-size: 0.85rem; }
  }
`;

export const EliminationButton = styled(motion.button)`
  background: transparent;
  border: none;
  color: ${({ theme, active }) => active ? 'var(--errorText)' : theme.textSecondary};
  opacity: ${({ active }) => active ? 1 : 0.3};
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    opacity: 1;
    background: ${({ theme }) => theme.hover};
  }
`;

export const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

export const ModalContent = styled(motion.div)`
  background: ${({ theme }) => theme.cardBg};
  border: 1px solid ${({ theme }) => theme.cardBorder};
  border-radius: var(--radius-md);
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
  box-shadow: var(--shadow-lg);
`;

export const QuestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 12px;
  margin-top: 24px;
`;

export const QuestionCell = styled.button`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-xs);
  background: ${({ theme, status, current }) => {
    if (current) return theme.primary;
    if (status === 'answered') return theme.hover;
    return 'transparent';
  }};
  border: 2px solid ${({ theme, status, current }) => {
    if (current) return theme.primary;
    if (status === 'answered') return theme.primary;
    return theme.border;
  }};
  color: ${({ theme, status, current }) => {
    if (current) return '#fff';
    if (status === 'answered') return theme.primary;
    return theme.textSecondary;
  }};
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  position: relative;
  transition: background var(--dur) var(--ease), border-color var(--dur) var(--ease),
              color var(--dur) var(--ease), transform var(--dur-fast) var(--ease), box-shadow var(--dur) var(--ease);

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

`;

export const ResultsHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${({ theme }) => theme.bodyBg};
  padding: 20px 0;
  border-bottom: 1px solid ${({ theme }) => theme.cardBorder};
  margin-bottom: 32px;
  backdrop-filter: blur(12px);
`;

export const DomainHeader = styled.div`
  padding: 12px 20px;
  background: ${({ theme }) => theme.isDark ? '#1e293b' : '#f8fafc'};
  border-left: 4px solid ${({ theme }) => theme.primary};
  margin: 32px 0 16px;
  border-radius: 4px;
  
  h4 {
    margin: 0;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.text};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

export const HighlightedCard = styled(motion.div)`
  animation: ${({ isHighlighted }) => isHighlighted ? pulse : 'none'} 1.5s ease-out;
  border: ${({ isHighlighted, theme }) => isHighlighted ? `2px solid ${theme.primary}` : `1px solid ${theme.cardBorder}`};
`;

export const DrawerOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
`;

export const DrawerContent = styled(motion.div)`
  width: 100%;
  max-width: 600px;
  height: 100%;
  background: ${({ theme }) => theme.cardBg};
  border-left: 1px solid ${({ theme }) => theme.cardBorder};
  box-shadow: -10px 0 30px rgba(0,0,0,0.2);
  padding: 40px;
  overflow-y: auto;
  position: relative;
`;

export const FocusModeContainer = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 0;
`;

export const StepperContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.cardBorder};
`;

export const ListRow = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: ${({ theme, active }) => active ? theme.hover : theme.cardBg};
  border-bottom: 1px solid ${({ theme }) => theme.cardBorder};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.hover};
  }
  
  .q-num {
    font-weight: 800;
    width: 40px;
    color: ${({ theme }) => theme.textSecondary};
  }
  
  .q-text {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 16px;
    font-weight: 500;
  }
`;

export const PageTransition = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.4, ease: "easeOut" }
};
